import { createRoot } from 'react-dom/client';
import '@fontsource/ibm-plex-sans/latin-400.css';
import '@fontsource/ibm-plex-sans/latin-500.css';
import '@fontsource/ibm-plex-sans/latin-600.css';
import '@fontsource/ibm-plex-mono/latin-400.css';
import '@fontsource/ibm-plex-mono/latin-500.css';
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/jetbrains-mono/latin-400.css';
import '@fontsource/jetbrains-mono/latin-500.css';
import '@fontsource/source-serif-4/latin-400.css';
import '@fontsource/source-serif-4/latin-500.css';
import '@fontsource/source-serif-4/latin-600.css';
import '@fontsource/source-code-pro/latin-400.css';
import '@fontsource/source-code-pro/latin-500.css';
// Atkinson Hyperlegible ships only 400 and 700, so those are the weights imported.
import '@fontsource/atkinson-hyperlegible/latin-400.css';
import '@fontsource/atkinson-hyperlegible/latin-700.css';
import '@fontsource/atkinson-hyperlegible-mono/latin-400.css';
import '@fontsource/atkinson-hyperlegible-mono/latin-500.css';
import '@fontsource-variable/instrument-sans';
import '@tallyframe/renderer/styles.css';
import './ui/theme.css';
import './styles/base.css';
import { createQueryClient } from './api/queries';
import { AppRoot } from './app/AppRoot';
import { createAppRouter } from './app/routes';
import { createSessionStore } from './app/session';
import { loadSupabaseAuth } from './auth/supabase';

/**
 * Entry point.
 *
 * Fonts load first (Latin-only IBM Plex Sans 400/500/600, IBM Plex Mono
 * 400/500 and variable Instrument Sans), then the renderer's stylesheet,
 * imported once here — it carries GridStack's own CSS bundled into it, so
 * hosts need exactly one import (contracts §5). The renderer declares its
 * tokens at zero specificity on `.tf-root` (among other selectors); `#root`
 * carries that class (see index.html), so the app's own style partials,
 * imported last, can reference those tokens directly instead of copying them
 * into `:root` (spec §4.1).
 *
 * `StrictMode` is deliberately absent: its double-invocation of effects mounts
 * and unmounts the GridStack instance twice on every render pass, which the
 * grid's imperative DOM handling does not survive cleanly. The tradeoff is
 * worth naming rather than leaving as an accident.
 */
const container = document.getElementById('root');
if (!container) throw new Error('index.html is missing its #root element.');

// One of each per tab: the session store, the query cache and the router.
const session = createSessionStore({
  storage: sessionStorage,
  loadSupabase: loadSupabaseAuth,
  origin: window.location.origin
});
const queryClient = createQueryClient();
const router = createAppRouter({ session, queryClient });

createRoot(container).render(<AppRoot session={session} queryClient={queryClient} router={router} />);
